import { fromNested, Nested, toNested } from 'base/webflow/nested';
import { ComponentStyle } from 'base/webflow/schema';

export function applyRelumeStyleHacks(
  componentSlug: string,
  styles: readonly ComponentStyle[],
) {
  if (!HACKS_COMPONENT_SLUGS.has(componentSlug)) {
    return styles;
  }
  return fromNested(applyRelumeStyleHacksToNested(toNested(styles)));
}

function applyRelumeStyleHacksToNested(
  styles: readonly Nested<ComponentStyle>[],
  prefix = '',
): Nested<ComponentStyle>[] {
  return styles.map((style) => {
    const myName = prefix + '.' + style.name;
    style = style.children.length
      ? ({
          ...style,
          children: applyRelumeStyleHacksToNested(style.children, myName),
        } as Nested<ComponentStyle>)
      : style;
    const hack = HACKS_BY_CLASSNAME[myName];
    return hack ? hack(style) : style;
  });
}

type StyleMapper = (style: Nested<ComponentStyle>) => Nested<ComponentStyle>;
const overrideHeight =
  (to: string): StyleMapper =>
  (style) =>
    applyCssRegex(style, /height:[^;]*;/, `height: ${to};`);

const addCss =
  (css: string): StyleMapper =>
  (style) => ({
    ...style,
    styleLess: `${style.styleLess};${css}`,
  });

function applyCssRegex(
  style: Nested<ComponentStyle>,
  regex: RegExp,
  replacer: string,
  expectedChangeError?: string,
): Nested<ComponentStyle> {
  const before = style.styleLess;
  const after = before.replace(regex, replacer);
  let madeChange = before !== after;

  const newVariants = Object.entries(style.variants).map(([name, variant]) => {
    const before = variant.styleLess;
    const after = before.replace(regex, replacer);
    madeChange = madeChange || before !== after;
    return [name, { ...variant, styleLess: after }] as const;
  });

  if (!madeChange && expectedChangeError) {
    throw new Error(expectedChangeError);
  }

  return {
    ...style,
    styleLess: after,
    variants: Object.fromEntries(newVariants),
  };
}

/**
 * VERY SAD
 *
 * The webflow components are built to be viewed through a viewport.  Blocks
 * is a canvas app so we're showing the "hidden" bits.
 *
 * Some of the components are incompatible with that idea.  Especially the
 * ones with a scrolling effect.  So we add hacks as a LAST RESORT ONLY.
 **/
const HACKS_BY_CLASSNAME: Record<string, StyleMapper> = {
  /**
   * These components are taller than needed so that there's room for the scroll
   * animation to play out.  Since we don't play the scroll animation, we don't
   * need to make them that tall.
   */
  '.gallery24_component': overrideHeight('100vh'),
  '.header75_component': overrideHeight('190vh'),
  '.header80_component': overrideHeight('100vh'),
  '.header81_component': overrideHeight('100vh'),
  '.header82_component': overrideHeight('190vh'),
  '.header83_component': overrideHeight('100vh'),
  '.header108_component': overrideHeight('160vh'),
  /**
   * This has a sticky 100vh video background.  Since we render more than 100vh
   * of the component, we move the background to the parent.
   * The component uses transparent backgrounds.  We use black to ensure there
   * are not 50 shades of grey.
   **/
  '.section_layout355': addCss('background: black;'),

  /**
   * This component has images which are sticky, but switch as you scroll.
   **/
  '.layout348_image.is-image2': addCss('top: 100vh; height: 100vh'),
  '.layout348_image.is-image3': addCss('top: 200vh; height: 100vh'),
  '.layout348_image.is-image4': addCss('top: 300vh; height: 100vh'),
  '.layout349_image.is-image2': addCss('top: 100vh; height: 100vh'),
  '.layout349_image.is-image3': addCss('top: 200vh; height: 100vh'),
  '.layout349_image.is-image4': addCss('top: 300vh; height: 100vh'),
  '.layout350_image.is-image2': addCss('top: 100vh; height: 100vh'),
  '.layout350_image.is-image3': addCss('top: 200vh; height: 100vh'),
  '.layout350_image.is-image4': addCss('top: 300vh; height: 100vh'),

  /**
   * This component embeds custom css code, which we don't retarget.
   * Because webflow designer don't let you add this property:
   **/
  '.layout351_column-vertical-text': addCss('writing-mode: vertical-rl'),

  /**
   * Another funky scrolling component, the header gets pushed too low.
   **/
  '.layout353_content-left': addCss('top: 0'),
  '.testimonial32_content-left': addCss('top: 0'),
};

/**
 * Component slugs to which we apply hacks.
 * Otherwise the code path gets skipped, because it's pretty expensive and unoptimised.
 */
const HACKS_COMPONENT_SLUGS = new Set([
  'section_gallery24',
  'section_header75',
  'section_header80',
  'section_header81',
  'section_header82',
  'section_header83',
  'section_header108',
  'section_layout355',
  'section_layout348',
  'section_layout349',
  'section_layout350',
  'section_layout351',
  'section_layout353',
  'section_testimonial32',
]);

// Exclude components where elements are expected to extend beyond the container
// bounds (e.g. dropdowns).
export const shouldComponentUseContainerBounds = (slug: string): boolean =>
  !(
    slug.match(/^card-header/) ||
    slug.match(/^navbar/) ||
    slug.match(/^section_multi-form/) ||
    slug.match(/^stacked-list/) ||
    slug.match(/^stat/) ||
    slug.match(/^topbar/) ||
    slug.match(/modal/) ||
    slug === 'section_layout356' ||
    slug === 'section_layout425'
  );
